import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Power, Globe, Mail, User, CheckCircle, XCircle, AlertCircle, Loader } from 'lucide-react';
import { toast } from 'react-toastify';
import ModalDialog from './common/ModalDialog';

import { jiraAccountService } from '../services/jiraAccountService';

const JiraAccountManager = ({ onJiraAccountChange, onAccountStatusChange }) => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [testingConnections, setTestingConnections] = useState(new Set());
  const [connectionResults, setConnectionResults] = useState({});
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    description: '',
    confirmLabel: 'Confirm',
    variant: 'danger',
    onConfirm: null,
  });

  const [formData, setFormData] = useState({
    name: '',
    domain: '',
    email: '',
    api_token: ''
  });

  const updateAccountStatus = React.useCallback((accountsList, connectionResultsMap) => {
    if (!onAccountStatusChange) return;

    const hasAccounts = accountsList.length > 0;
    const activeAccount = accountsList.find(acc => acc.active);
    const hasAccountsButNoneActive = hasAccounts && !activeAccount;
    const activeAccountConnectionFailed = activeAccount &&
      connectionResultsMap[activeAccount.id] &&
      !connectionResultsMap[activeAccount.id].success;

    onAccountStatusChange({
      hasAccounts,
      hasAccountsButNoneActive,
      activeAccountConnectionFailed
    });
  }, [onAccountStatusChange]);

  // Load accounts on component mount
  useEffect(() => {
    fetchAccounts();
  }, []);

  useEffect(() => {
    updateAccountStatus(accounts, connectionResults);
  }, [accounts, connectionResults, updateAccountStatus]);

  // Fetch accounts from API
  const fetchAccounts = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await jiraAccountService.getAccounts();
      const accountsList = Array.isArray(response?.data) ? response.data : [];
      setAccounts(accountsList);

      const activeAccount = accountsList.find(acc => acc.active);
      onJiraAccountChange(activeAccount);
      updateAccountStatus(accountsList, connectionResults);

      accountsList.forEach(account => {
        testConnection(account.id, true).catch(err => {
          console.error(`Connection test failed for account ${account.id}:`, err.message);
        });
      });
    } catch (err) {
      setError(err.message || 'Failed to fetch accounts');
      if (onAccountStatusChange) {
        onAccountStatusChange({ hasAccounts: false, activeAccountConnectionFailed: false });
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      domain: '',
      email: '',
      api_token: ''
    });
    setEditingAccount(null);
    setShowAddForm(false);
  };

  // Handle form submission
  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = editingAccount
        ? await jiraAccountService.updateAccount(editingAccount.id, formData)
        : await jiraAccountService.createAccount(formData);

      await fetchAccounts();
      resetForm();

      if (editingAccount) {
        toast.success('Account updated successfully');
      } else {
        toast.success('Account created successfully');
      }

      if (response?.data?.id) {
        testConnection(response.data.id).catch(err => {
          console.error('Connection test failed after save:', err.message);
        });
      }
    } catch (err) {
      const errorMsg = err.message || 'Failed to save account';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Start editing an account
  const startEdit = (account) => {
    setEditingAccount(account);
    setFormData({
      name: account.name,
      domain: account.domain,
      email: account.email,
      api_token: '' // Don't pre-fill API token for security
    });
    setShowAddForm(true);
  };

  // Delete account
  const deleteAccount = (accountId) => {
    const account = accounts.find(acc => acc.id === accountId);
    const accountName = account?.name || 'this account';

    setConfirmDialog({
      open: true,
      title: 'Delete Jira Account',
      description: `Are you sure you want to delete "${accountName}"? This action cannot be undone.`,
      confirmLabel: 'Delete',
      variant: 'danger',
      loading: false,
      onConfirm: async () => {
        setLoading(true);
        setConfirmDialog(prev => ({ ...prev, loading: true }));
        try {
          await jiraAccountService.deleteAccount(accountId);
          setConnectionResults(prev => {
            const newResults = { ...prev };
            delete newResults[accountId];
            return newResults;
          });
          await fetchAccounts();
          toast.success('Account deleted successfully');
        } catch (err) {
          const errorMsg = err.message || 'Failed to delete account';
          setError(errorMsg);
          toast.error(errorMsg);
          throw err;
        } finally {
          setLoading(false);
          setConfirmDialog(prev => ({ ...prev, loading: false }));
        }
      },
    });
  };

  // Activate account
  const activateAccount = async (accountId) => {
    setLoading(true);
    try {
      await jiraAccountService.activateAccount(accountId);
      await fetchAccounts();
      toast.success('Account activated successfully');
    } catch (err) {
      const errorMsg = err.message || 'Failed to activate account';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Test connection
  const testConnection = async (accountId, silent = false) => {
    setTestingConnections(prev => new Set([...prev, accountId]));

    try {
      const response = await jiraAccountService.testConnection(accountId);
      setConnectionResults(prev => {
        const newResults = {
          ...prev,
          [accountId]: response
        };
        updateAccountStatus(accounts, newResults);
        return newResults;
      });

      if (!silent) {
        if (response.success) {
          toast.success(`Connection successful! Connected as: ${response.user?.display_name || 'Unknown'}`);
        } else {
          // Temp: generic message; TODO show specific guidance (domain vs email vs token)
          toast.error(
            'Connection failed. Please review your domain, email, and API token.',
            { autoClose: 6000 }
          );
        }
      }
    } catch (err) {
      const errorResult = { success: false, error: err.message };
      setConnectionResults(prev => {
        const newResults = {
          ...prev,
          [accountId]: errorResult
        };
        updateAccountStatus(accounts, newResults);
        return newResults;
      });

      if (!silent) {
        // Temp: generic message; TODO show specific guidance (domain vs email vs token)
        toast.error(
          'Connection failed. Please review your domain, email, and API token.',
          { autoClose: 6000 }
        );
      }
    } finally {
      setTestingConnections(prev => {
        const newSet = new Set(prev);
        newSet.delete(accountId);
        return newSet;
      });
    }
  };

  // Get connection status icon
  const getConnectionIcon = (accountId) => {
    const result = connectionResults[accountId];
    const isTesting = testingConnections.has(accountId);

    if (isTesting) {
      return <Loader className="w-4 h-4 text-blue-500 animate-spin" />;
    }

    if (!result) {
      return <AlertCircle className="w-4 h-4 text-gray-400 dark:text-slate-500" />;
    }

    return result.success ?
      <CheckCircle className="w-4 h-4 text-green-500" /> :
      <XCircle className="w-4 h-4 text-red-500" />;
  };

  // Get connection status color
  const getConnectionBorderColor = (accountId) => {
    const result = connectionResults[accountId];
    const isTesting = testingConnections.has(accountId);

    if (isTesting) return 'border-blue-300 dark:border-blue-800/50';
    if (!result) return 'border-gray-200 dark:border-white/10';
    return result.success
      ? 'border-green-300 dark:border-green-800/50'
      : 'border-red-300 dark:border-red-800/50';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-100">
            Your Accounts
          </h2>
        </div>

        <button
          onClick={() => setShowAddForm(true)}
          disabled={loading}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Account
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="rounded-lg p-4 border bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800/50">
          <div className="flex items-center">
            <XCircle className="w-5 h-5 text-red-500 mr-2" />
            <span className="text-red-800 dark:text-red-300">{error}</span>
          </div>
        </div>
      )}

      {/* Add/Edit Form */}
      {showAddForm && (
        <div className="rounded-lg p-6 shadow-sm border bg-white dark:bg-slate-800 border-gray-200 dark:border-white/10">
          <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-slate-100">
            {editingAccount ? 'Edit Account' : 'Add New Account'}
          </h3>

          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-slate-300">
                  Account Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 border-gray-300 dark:bg-slate-900 dark:border-white/20 dark:text-slate-100 dark:placeholder-slate-500"
                  placeholder="e.g., Work Jira, Personal Project"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-slate-300">
                  Domain
                </label>
                <input
                  type="text"
                  value={formData.domain}
                  onChange={(e) => handleInputChange('domain', e.target.value)}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 border-gray-300 dark:bg-slate-900 dark:border-white/20 dark:text-slate-100 dark:placeholder-slate-500"
                  placeholder="company.atlassian.net"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-slate-300">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 border-gray-300 dark:bg-slate-900 dark:border-white/20 dark:text-slate-100 dark:placeholder-slate-500"
                  placeholder="your.email@company.com"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-slate-300">
                  API Token
                </label>
                <input
                  type="password"
                  value={formData.api_token}
                  onChange={(e) => handleInputChange('api_token', e.target.value)}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 border-gray-300 dark:bg-slate-900 dark:border-white/20 dark:text-slate-100 dark:placeholder-slate-500"
                  placeholder="Your Jira API token"
                  required={!editingAccount}
                />
              </div>
            </div>

            <div className="rounded-lg p-4 border bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-900/30">
              <p className="text-sm text-blue-800 dark:text-blue-200">
                <strong>Get your API token:</strong> Visit{' '}
                <a
                  href="https://id.atlassian.com/manage-profile/security/api-tokens"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-blue-900 dark:hover:text-blue-100"
                >
                  Atlassian API Tokens
                </a>{' '}
                to create a new token for this application.
              </p>
            </div>

            <div className="flex items-center justify-end gap-3 flex-wrap">
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 rounded-lg transition-colors text-gray-700 dark:text-slate-200 bg-gray-100 dark:bg-slate-700 hover:bg-gray-200 dark:hover:bg-slate-600"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Saving...' : (editingAccount ? 'Update Account' : 'Add Account')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && accounts.length === 0 && (
        <div className="text-center py-8">
          <Loader className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-2" />
          <p className="text-gray-600 dark:text-slate-400">Loading accounts...</p>
        </div>
      )}

      {/* Accounts List */}
      {accounts.length > 0 && (
        <div className="space-y-4">
          {accounts.map(account => (
            <div
              key={account.id}
              className={`border rounded-lg p-6 transition-all ${
                account.active
                  ? 'border-green-300 dark:border-green-800/50 bg-green-50 dark:bg-green-900/20'
                  : `${getConnectionBorderColor(account.id)} bg-white dark:bg-slate-800`
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className={`text-lg font-semibold ${
                      account.active
                        ? 'text-green-800 dark:text-green-300'
                        : 'text-gray-900 dark:text-slate-100'
                    }`}>
                      {account.name}
                    </h3>
                    {account.active && (
                      <span className="inline-flex items-center px-2 py-1 text-xs rounded-full bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Active
                      </span>
                    )}
                    {getConnectionIcon(account.id)}
                  </div>

                  <div className="space-y-1 text-sm text-gray-600 dark:text-slate-400">
                    <div className="flex items-center space-x-2">
                      <Globe className="w-4 h-4" />
                      <span>{account.domain}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Mail className="w-4 h-4" />
                      <span>{account.email}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <User className="w-4 h-4" />
                      <span>Created: {new Date(account.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>

                  {/* Connection Status */}
                  {connectionResults[account.id] && (
                    <div className="mt-3">
                      {connectionResults[account.id].success ? (
                        <div className="text-sm text-green-600 dark:text-green-300">
                          ✅ Connected as: {connectionResults[account.id].user?.display_name || 'Unknown'}
                        </div>
                      ) : (
                        // Temp: generic message; TODO show specific guidance (domain vs email vs token)
                        <div className="text-sm text-red-600 dark:text-red-300">
                          <div>Connection failed. Please review your domain, email, and API token.</div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                  <button
                    onClick={() => testConnection(account.id)}
                    disabled={testingConnections.has(account.id)}
                    className="p-2 rounded-lg transition-colors disabled:opacity-50 text-blue-600 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                    title="Test Connection"
                  >
                    <Power className="w-4 h-4" />
                  </button>

                  <button
                    onClick={() => startEdit(account)}
                    className="p-2 rounded-lg transition-colors text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700"
                    title="Edit Account"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>

                  {!account.active && (
                    <button
                      onClick={() => activateAccount(account.id)}
                      disabled={loading}
                      className="px-3 py-1 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                    >
                      Activate
                    </button>
                  )}

                  <button
                    onClick={() => deleteAccount(account.id)}
                    disabled={loading}
                    className="p-2 rounded-lg transition-colors disabled:opacity-50 text-red-600 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20"
                    title="Delete Account"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {accounts.length === 0 && !loading && (
        <div className="text-center py-12 border-2 border-dashed rounded-lg border-gray-300 dark:border-white/20">
          <Globe className="w-12 h-12 mx-auto mb-4 text-gray-400 dark:text-slate-500" />
          <h3 className="text-lg font-medium mb-2 text-gray-900 dark:text-slate-100">No Jira Accounts</h3>
          <p className="text-gray-600 dark:text-slate-400 mb-4">
            Add your first Jira account to start managing your projects and issues.
          </p>
          <button
            onClick={() => setShowAddForm(true)}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Your First Account
          </button>
        </div>
      )}

      <ModalDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        description={confirmDialog.description}
        confirmLabel={confirmDialog.confirmLabel}
        cancelLabel="Cancel"
        variant={confirmDialog.variant}
        loading={confirmDialog.loading}
        onConfirm={confirmDialog.onConfirm}
        onClose={() =>
          setConfirmDialog(prev => ({
            ...prev,
            open: false,
          }))
        }
      />
    </div>
  );
};

export default JiraAccountManager;